import React, { useState, useEffect, useCallback } from 'react';
import { medicalDocumentsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Heart,
  AlertTriangle,
  Pill,
  History,
  Phone,
  User,
  Droplets,
  Edit3,
  Save,
  X,
  Plus,
  Stethoscope,
  FlaskConical,
  Scan,
  Syringe,
  FileText,
  Calendar,
  Loader2,
  Eye,
  Download,
  Shield,
  ClipboardList,
  Send,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const NOTE_TYPES = [
  { value: 'observation', label: 'Observation clinique' },
  { value: 'diagnostic', label: 'Diagnostic' },
  { value: 'recommendation', label: 'Recommandation' },
  { value: 'follow_up', label: 'Suivi' }
];

/**
 * PatientMedicalRecord - Component for professionals to view/edit patient's medical record
 * 
 * Props:
 * - patientId: string - ID of the patient
 * - appointmentId?: string - Optional appointment ID for context
 * - onClose?: function - Callback when modal/drawer is closed
 * - mode?: 'modal' | 'inline' - Display mode
 */
const PatientMedicalRecord = ({ patientId, appointmentId, onClose, mode = 'inline' }) => {
  // State
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  const [profile, setProfile] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [groupedTimeline, setGroupedTimeline] = useState({});
  
  // Edit mode
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    blood_type: '',
    allergies: [],
    known_conditions: [],
    current_treatments: [],
    medical_history: []
  });
  const [newAllergy, setNewAllergy] = useState('');
  const [newCondition, setNewCondition] = useState('');
  const [newTreatment, setNewTreatment] = useState('');
  const [newHistory, setNewHistory] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Add note
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteForm, setNoteForm] = useState({
    title: '',
    content: '',
    note_type: 'observation'
  });
  const [addingNote, setAddingNote] = useState(false);
  
  // Document view
  const [showDocModal, setShowDocModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Load data
  const loadData = useCallback(async () => {
    if (!patientId) return;
    
    setLoading(true);
    try {
      // Load patient profile
      const profileRes = await medicalDocumentsAPI.getPatientProfile(patientId);
      setPatient(profileRes.data.patient);
      setProfile(profileRes.data.profile);
      
      // Load timeline
      const timelineRes = await medicalDocumentsAPI.getPatientTimeline(patientId);
      setTimeline(timelineRes.data.timeline || []);
      setGroupedTimeline(timelineRes.data.grouped || {});
    } catch (error) {
      console.error('Error loading patient data:', error);
      toast.error('Erreur lors du chargement du dossier patient');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Edit profile
  const startEditProfile = () => {
    if (profile) {
      setProfileForm({
        blood_type: profile.blood_type || '',
        allergies: profile.allergies || [],
        known_conditions: profile.known_conditions || [],
        current_treatments: profile.current_treatments || [],
        medical_history: profile.medical_history || []
      });
    }
    setEditingProfile(true);
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const params = {
        blood_type: profileForm.blood_type || null,
        allergies: JSON.stringify(profileForm.allergies),
        known_conditions: JSON.stringify(profileForm.known_conditions),
        current_treatments: JSON.stringify(profileForm.current_treatments),
        medical_history: JSON.stringify(profileForm.medical_history),
        notes: `Mis à jour lors de la consultation`
      };
      
      await medicalDocumentsAPI.updatePatientProfile(patientId, params);
      toast.success('Dossier patient mis à jour');
      setEditingProfile(false);
      loadData();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingProfile(false);
    }
  };

  // Add item to array
  const addToArray = (field, value, setter) => {
    if (value.trim()) {
      setProfileForm(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      setter('');
    }
  };

  const removeFromArray = (field, index) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // Add note
  const handleAddNote = async () => {
    if (!noteForm.title.trim() || !noteForm.content.trim()) {
      toast.error('Veuillez remplir le titre et le contenu');
      return;
    }
    
    setAddingNote(true);
    try {
      await medicalDocumentsAPI.addPatientNote(patientId, {
        title: noteForm.title,
        content: noteForm.content,
        note_type: noteForm.note_type,
        appointment_id: appointmentId || ''
      });
      
      toast.success('Note ajoutée au dossier');
      setShowAddNote(false);
      setNoteForm({ title: '', content: '', note_type: 'observation' });
      loadData();
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Erreur lors de l\'ajout de la note');
    } finally {
      setAddingNote(false);
    }
  };

  // Get icon for event type
  const getEventIcon = (event) => {
    const iconMap = {
      'stethoscope': Stethoscope,
      'flask': FlaskConical,
      'scan': Scan,
      'syringe': Syringe,
      'file-text': FileText,
      'clipboard': ClipboardList,
      'file': FileText
    };
    return iconMap[event.icon] || FileText;
  };

  const getColorClasses = (color) => {
    const colors = {
      'blue': 'bg-blue-100 text-blue-600 border-blue-200',
      'green': 'bg-green-100 text-green-600 border-green-200',
      'purple': 'bg-purple-100 text-purple-600 border-purple-200',
      'orange': 'bg-orange-100 text-orange-600 border-orange-200',
      'indigo': 'bg-indigo-100 text-indigo-600 border-indigo-200',
      'amber': 'bg-amber-100 text-amber-600 border-amber-200',
      'slate': 'bg-slate-100 text-slate-600 border-slate-200'
    };
    return colors[color] || colors.slate;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1976D2]" />
      </div>
    );
  }

  const content = (
    <div className="space-y-4" data-testid="patient-medical-record">
      {/* Patient Info Header */}
      <div className="flex items-center justify-between pb-3 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#1976D2] flex items-center justify-center text-white font-bold text-lg">
            {patient?.full_name?.charAt(0) || 'P'}
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">{patient?.full_name || 'Patient'}</h3>
            <p className="text-sm text-slate-500">
              {patient?.date_of_birth && `Né(e) le ${new Date(patient.date_of_birth).toLocaleDateString('fr-FR')}`}
              {patient?.gender && ` • ${patient.gender === 'male' ? 'Homme' : 'Femme'}`}
            </p>
          </div>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        )}
      </div>

      {/* ===== INFORMATIONS IMPORTANTES ===== */}
      <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-slate-800 flex items-center gap-2 text-sm">
              <Shield className="w-4 h-4 text-[#1976D2]" />
              Informations importantes
            </h4>
            {!editingProfile ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={startEditProfile}
                className="text-[#1976D2] text-xs h-7"
                data-testid="edit-patient-profile-btn"
              >
                <Edit3 className="w-3 h-3 mr-1" />
                Compléter
              </Button>
            ) : (
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => setEditingProfile(false)} className="h-7">
                  <X className="w-3 h-3" />
                </Button>
                <Button
                  size="sm"
                  onClick={saveProfile}
                  disabled={savingProfile}
                  className="bg-[#059669] hover:bg-[#047857] h-7 text-xs"
                  data-testid="save-patient-profile-btn"
                >
                  {savingProfile ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                  Enregistrer
                </Button>
              </div>
            )}
          </div>

          {editingProfile ? (
            /* ===== EDIT MODE ===== */
            <div className="space-y-3">
              {/* Blood Type */}
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  <Droplets className="w-3 h-3" /> Groupe sanguin
                </Label>
                <Select
                  value={profileForm.blood_type}
                  onValueChange={(v) => setProfileForm(p => ({ ...p, blood_type: v }))}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {BLOOD_TYPES.map(bt => (
                      <SelectItem key={bt} value={bt}>{bt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Allergies */}
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  <AlertTriangle className="w-3 h-3" /> Allergies
                </Label>
                <div className="flex flex-wrap gap-1 mb-1">
                  {profileForm.allergies.map((a, i) => (
                    <Badge key={i} variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                      {a}
                      <button onClick={() => removeFromArray('allergies', i)} className="ml-1">
                        <X className="w-2 h-2" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-1">
                  <Input
                    value={newAllergy}
                    onChange={(e) => setNewAllergy(e.target.value)}
                    placeholder="Ajouter allergie"
                    className="h-8 text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('allergies', newAllergy, setNewAllergy)}
                  />
                  <Button size="sm" variant="outline" className="h-8" onClick={() => addToArray('allergies', newAllergy, setNewAllergy)}>
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              {/* Known Conditions */}
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  <Heart className="w-3 h-3" /> Maladies connues
                </Label>
                <div className="flex flex-wrap gap-1 mb-1">
                  {profileForm.known_conditions.map((c, i) => (
                    <Badge key={i} variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
                      {c}
                      <button onClick={() => removeFromArray('known_conditions', i)} className="ml-1">
                        <X className="w-2 h-2" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-1">
                  <Input
                    value={newCondition}
                    onChange={(e) => setNewCondition(e.target.value)}
                    placeholder="Ex: Diabète, HTA..."
                    className="h-8 text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('known_conditions', newCondition, setNewCondition)}
                  />
                  <Button size="sm" variant="outline" className="h-8" onClick={() => addToArray('known_conditions', newCondition, setNewCondition)}>
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              {/* Current Treatments */}
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  <Pill className="w-3 h-3" /> Traitements en cours
                </Label>
                <div className="flex flex-wrap gap-1 mb-1">
                  {profileForm.current_treatments.map((t, i) => (
                    <Badge key={i} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                      {t}
                      <button onClick={() => removeFromArray('current_treatments', i)} className="ml-1">
                        <X className="w-2 h-2" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-1">
                  <Input
                    value={newTreatment}
                    onChange={(e) => setNewTreatment(e.target.value)}
                    placeholder="Ex: Metformine 500mg"
                    className="h-8 text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('current_treatments', newTreatment, setNewTreatment)}
                  />
                  <Button size="sm" variant="outline" className="h-8" onClick={() => addToArray('current_treatments', newTreatment, setNewTreatment)}>
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              {/* Medical History */}
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  <History className="w-3 h-3" /> Antécédents
                </Label>
                <div className="flex flex-wrap gap-1 mb-1">
                  {profileForm.medical_history.map((h, i) => (
                    <Badge key={i} variant="outline" className="bg-slate-100 text-slate-700 border-slate-200 text-xs">
                      {h}
                      <button onClick={() => removeFromArray('medical_history', i)} className="ml-1">
                        <X className="w-2 h-2" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-1">
                  <Input
                    value={newHistory}
                    onChange={(e) => setNewHistory(e.target.value)}
                    placeholder="Ex: Appendicectomie (2019)"
                    className="h-8 text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('medical_history', newHistory, setNewHistory)}
                  />
                  <Button size="sm" variant="outline" className="h-8" onClick={() => addToArray('medical_history', newHistory, setNewHistory)}>
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            /* ===== VIEW MODE ===== */
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-start gap-2">
                <Droplets className="w-3 h-3 text-red-500 mt-0.5" />
                <div>
                  <p className="text-slate-500">Groupe sanguin</p>
                  <p className="font-medium text-slate-900">
                    {profile?.blood_type || <span className="text-slate-400">—</span>}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5" />
                <div>
                  <p className="text-slate-500">Allergies</p>
                  {profile?.allergies?.length > 0 ? (
                    <p className="font-medium text-red-600">{profile.allergies.join(', ')}</p>
                  ) : (
                    <p className="text-slate-400">Aucune</p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Heart className="w-3 h-3 text-purple-500 mt-0.5" />
                <div>
                  <p className="text-slate-500">Maladies connues</p>
                  {profile?.known_conditions?.length > 0 ? (
                    <p className="font-medium text-slate-900">{profile.known_conditions.join(', ')}</p>
                  ) : (
                    <p className="text-slate-400">Aucune</p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Pill className="w-3 h-3 text-blue-500 mt-0.5" />
                <div>
                  <p className="text-slate-500">Traitements</p>
                  {profile?.current_treatments?.length > 0 ? (
                    <p className="font-medium text-slate-900">{profile.current_treatments.join(', ')}</p>
                  ) : (
                    <p className="text-slate-400">Aucun</p>
                  )}
                </div>
              </div>

              <div className="col-span-2 flex items-start gap-2">
                <History className="w-3 h-3 text-slate-500 mt-0.5" />
                <div>
                  <p className="text-slate-500">Antécédents</p>
                  {profile?.medical_history?.length > 0 ? (
                    <p className="font-medium text-slate-900">{profile.medical_history.join(', ')}</p>
                  ) : (
                    <p className="text-slate-400">Aucun</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ===== AJOUTER UNE NOTE ===== */}
      <Button
        onClick={() => setShowAddNote(true)}
        className="w-full bg-[#1976D2] hover:bg-[#1565C0] rounded-xl"
        data-testid="add-note-btn"
      >
        <ClipboardList className="w-4 h-4 mr-2" />
        Ajouter une note au dossier
      </Button>

      {/* ===== HISTORIQUE SANTÉ ===== */}
      <div>
        <h4 className="font-semibold text-slate-800 flex items-center gap-2 mb-3 text-sm">
          <Calendar className="w-4 h-4 text-[#1976D2]" />
          Historique santé
          <Badge variant="outline" className="text-xs ml-auto">
            {timeline.length} événement(s)
          </Badge>
        </h4>

        {Object.keys(groupedTimeline).length === 0 ? (
          <div className="text-center py-6 bg-slate-50 rounded-xl">
            <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">Aucun historique disponible</p>
          </div>
        ) : (
          <ScrollArea className="h-[300px]">
            <div className="space-y-4 pr-2">
              {Object.entries(groupedTimeline).map(([period, events]) => (
                <div key={period}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-slate-600">{period}</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>

                  <div className="space-y-2">
                    {events.map((event) => {
                      const Icon = getEventIcon(event);
                      const colorClasses = getColorClasses(event.color);
                      
                      return (
                        <div
                          key={event.id}
                          className="flex items-start gap-2 p-2 bg-white rounded-lg border border-slate-100 hover:border-slate-200 transition-colors cursor-pointer"
                          onClick={() => {
                            if (event.details?.file_url) {
                              setSelectedDoc(event);
                              setShowDocModal(true);
                            }
                          }}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClasses}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 text-sm truncate">{event.title}</p>
                            <p className="text-xs text-slate-500">{event.subtitle}</p>
                            {event.details?.diagnosis && (
                              <p className="text-xs text-slate-600 mt-1 bg-slate-50 p-1 rounded">
                                {event.details.diagnosis}
                              </p>
                            )}
                            {event.details?.content && (
                              <p className="text-xs text-slate-600 mt-1">
                                {event.details.content}
                              </p>
                            )}
                          </div>
                          {event.details?.file_url && (
                            <Eye className="w-4 h-4 text-slate-400" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Add Note Modal */}
      <Dialog open={showAddNote} onOpenChange={setShowAddNote}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter une note clinique</DialogTitle>
            <DialogDescription>
              Cette note sera ajoutée au dossier de {patient?.full_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Type de note</Label>
              <Select
                value={noteForm.note_type}
                onValueChange={(v) => setNoteForm(p => ({ ...p, note_type: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NOTE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Titre</Label>
              <Input
                value={noteForm.title}
                onChange={(e) => setNoteForm(p => ({ ...p, title: e.target.value }))}
                placeholder="Ex: Examen clinique, Suivi tension..."
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Contenu</Label>
              <Textarea
                value={noteForm.content}
                onChange={(e) => setNoteForm(p => ({ ...p, content: e.target.value }))}
                placeholder="Détails de l'observation, diagnostic, recommandations..."
                className="mt-1"
                rows={4}
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowAddNote(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={handleAddNote}
                disabled={addingNote}
                className="flex-1 bg-[#1976D2] hover:bg-[#1565C0]"
              >
                {addingNote ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Ajouter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document view modal */}
      <Dialog open={showDocModal} onOpenChange={setShowDocModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedDoc?.title}</DialogTitle>
            <DialogDescription>{selectedDoc?.subtitle}</DialogDescription>
          </DialogHeader>
          
          {selectedDoc?.details?.file_url && (
            <div className="mt-4">
              {selectedDoc.details.file_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                <img
                  src={selectedDoc.details.file_url}
                  alt={selectedDoc.title}
                  className="w-full rounded-lg"
                />
              ) : (
                <div className="bg-slate-100 rounded-lg p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-600 mb-4">Document PDF</p>
                  <Button
                    onClick={() => window.open(selectedDoc.details.file_url, '_blank')}
                    className="bg-[#1976D2] hover:bg-[#1565C0]"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );

  if (mode === 'modal') {
    return (
      <Dialog open={true} onOpenChange={() => onClose?.()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return content;
};

export default PatientMedicalRecord;
